import { Heading, Box, Text, Flex, Button } from 'theme-ui'
import { useParams } from 'react-router'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useConversationData } from '../../util/conversation_data'
import { handleConversationDataUpdate } from '../../actions'
import Pagination from './Pagination'
import XidsInUseTable from './ParticipantManagement/XidsInUseTable.js'
import XidAllowListTable from './ParticipantManagement/XidAllowListTable.js'
import PolisNet from '../../util/net'
import Spinner from '../framework/Spinner'
import strings from '../../strings/strings'

const ParticipantManagement = () => {
  const params = useParams()
  const conversationData = useConversationData()
  const dispatch = useDispatch()
  const conversationId = useMemo(
    () => conversationData?.conversation_id || params.conversation_id,
    [conversationData?.conversation_id, params.conversation_id]
  )

  const [xids, setXids] = useState([])
  const [pagination, setPagination] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [limit] = useState(50)

  const useXidAllowList = Boolean(conversationData?.use_xid_whitelist)
  const xidRequired = Boolean(conversationData?.xid_required)
  const [activeTab, setActiveTab] = useState('inUse') // 'inUse' | 'allowList'

  const handleXidAllowListToggle = () => {
    const newValue = !useXidAllowList
    dispatch(handleConversationDataUpdate(conversationData, 'use_xid_whitelist', newValue))
  }

  const handleXidRequiredToggle = () => {
    const newValue = !xidRequired
    dispatch(handleConversationDataUpdate(conversationData, 'xid_required', newValue))
  }

  const loadXids = async (newOffset = 0) => {
    if (!conversationId) return
    setLoading(true)
    setError(null)

    try {
      const res = await PolisNet.polisGet('/api/v3/xids', {
        conversation_id: conversationId,
        limit,
        offset: newOffset
      })

      setXids(res?.xids || [])
      setPagination(res?.pagination || null)
    } catch (e) {
      setError(e?.responseText || e?.message || strings('dist_failed_load_xids'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (conversationId) {
      loadXids(0)
    }
  }, [conversationId])

  const handlePageChange = (newOffset) => {
    loadXids(newOffset)
  }

  return (
    <Box>
      <Heading
        as="h3"
        sx={{
          fontSize: [3, null, 4],
          lineHeight: 'body',
          mb: [3, null, 4]
        }}>
        {strings('dist_participant_management')}
      </Heading>
      <Text sx={{ mb: [3] }}>
        {`${strings('dist_manage_participants_for')} ${conversationId}.`}
      </Text>

      <Flex sx={{ alignItems: 'flex-start', mb: [2] }}>
        <Box sx={{ flexShrink: 0, position: 'relative', top: -0.5 }}>
          <input
            type="checkbox"
            data-testid="xid_required"
            checked={xidRequired}
            onChange={handleXidRequiredToggle}
            disabled={useXidAllowList}
          />
        </Box>
        <Box
          sx={{
            ml: [2],
            flex: '1 1 auto',
            maxWidth: ['100%', '100%', '35em'],
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
          <Text>{strings('dist_xid_required_to_vote')}</Text>
          {useXidAllowList && (
            <Text sx={{ ml: [2], color: 'mediumGray', fontSize: [0] }}>
              {strings('dist_xid_required_because_allow_list')}
            </Text>
          )}
        </Box>
      </Flex>

      <Flex sx={{ alignItems: 'flex-start', mb: [3] }}>
        <Box sx={{ flexShrink: 0, position: 'relative', top: -0.5 }}>
          <input
            type="checkbox"
            data-testid="use_xid_whitelist"
            checked={useXidAllowList}
            onChange={handleXidAllowListToggle}
          />
        </Box>
        <Box
          sx={{
            ml: [2],
            flex: '1 1 auto',
            maxWidth: ['100%', '100%', '35em'],
            wordWrap: 'break-word',
            overflowWrap: 'break-word'
          }}>
          <Text>{strings('dist_use_xid_allow_list')}</Text>
        </Box>
      </Flex>

      {/* Tab controls */}
      <Flex sx={{ gap: [2], mb: [3], flexWrap: 'wrap' }}>
        <Button
          variant={activeTab === 'inUse' ? 'primary' : 'outline'}
          size="small"
          onClick={() => setActiveTab('inUse')}>
          {strings('dist_xids_in_use')}
        </Button>
        <Button
          variant={activeTab === 'allowList' ? 'primary' : 'outline'}
          size="small"
          onClick={() => setActiveTab('allowList')}>
          {strings('dist_xids_allowed')}
        </Button>
      </Flex>

      {activeTab === 'inUse' && loading && !xids.length ? (
        <Spinner />
      ) : activeTab === 'inUse' && error ? (
        <Text sx={{ color: 'error', mb: [3] }}>{error}</Text>
      ) : activeTab === 'inUse' && xids.length === 0 ? (
        <Text sx={{ color: 'mediumGray', mb: [3] }}>{strings('dist_no_xids_found')}</Text>
      ) : activeTab === 'inUse' ? (
        <>
          <XidsInUseTable xids={xids} conversationId={conversationId} />
          <Pagination pagination={pagination} onPageChange={handlePageChange} loading={loading} />
        </>
      ) : (
        <XidAllowListTable conversationId={conversationId} />
      )}
    </Box>
  )
}

export default ParticipantManagement
